import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Input, useTabParam } from '@structural-climbing/ui'
import { ChipTabs } from '../design/ChipTabs'
import { useCompetitions } from '../features/competitions/useCompetitions'
import { useMyGyms } from '../features/gym/useMyGyms'
import { CompetitionCard } from '../features/competitions/CompetitionCard'
import type { CompetitionState, CompetitionSummary } from '../features/competitions/types'
import { Page } from '../design/Page'
import { EmptyState } from '../design/EmptyState'
import { SkeletonCard } from '../design/Skeleton'
import { staggerIndex } from '../design/motion'

const STATE_FILTERS = ['all', 'upcoming', 'finished'] as const
type StateFilter = (typeof STATE_FILTERS)[number]

// Live first, then what is coming, then the archive.
const STATE_ORDER: Record<CompetitionState, number> = { in_progress: 0, upcoming: 1, ended: 2 }

function matchesFilter(c: CompetitionSummary, filter: StateFilter): boolean {
  if (filter === 'all') return true
  // "Upcoming" covers anything not yet over, so a competition running right now
  // is not hidden by the filter a member reaches for to find it.
  if (filter === 'upcoming') return c.state !== 'ended'
  return c.state === 'ended'
}

function sortForDisplay(a: CompetitionSummary, b: CompetitionSummary): number {
  const byState = STATE_ORDER[a.state] - STATE_ORDER[b.state]
  if (byState !== 0) return byState
  // Upcoming reads soonest-first; the archive reads most-recent-first.
  return a.state === 'upcoming'
    ? a.starts_at.localeCompare(b.starts_at)
    : b.starts_at.localeCompare(a.starts_at)
}

export function CompetitionsPage() {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  // No gym id: this page spans every gym the member belongs to.
  const { competitions, loading } = useCompetitions(null, apiUrl, getAccessTokenSilently)
  const { gyms: myGyms } = useMyGyms()

  // Opens on what is still to come: a member arrives wanting the next
  // competition, not the archive. "Upcoming" includes one running right now.
  const [filter, setFilter] = useTabParam('tab', STATE_FILTERS, 'upcoming')
  const [search, setSearch] = useState('')

  const sections = useMemo(() => {
    const q = search.trim().toLowerCase()
    const visible = competitions
      .filter((c) => matchesFilter(c, filter))
      .filter(
        (c) =>
          !q || c.name.toLowerCase().includes(q) || (c.gym_name ?? '').toLowerCase().includes(q),
      )
      .sort(sortForDisplay)

    // Sections are about your relationship to a competition; the tabs above
    // filter by state. Keeping the two apart avoids a card appearing twice.
    const myGymIds = new Set(myGyms.map((g) => g.id))
    return [
      { key: 'mine', items: visible.filter((c) => c.joined) },
      {
        key: 'myGyms',
        items: visible.filter((c) => !c.joined && myGymIds.has(c.gym_id)),
      },
      // Competitions at gyms the member does not belong to, which they can
      // still enter. Someone who belongs to no gym sees only this one.
      {
        key: 'open',
        items: visible.filter((c) => !c.joined && !myGymIds.has(c.gym_id)),
      },
    ].filter((s) => s.items.length > 0)
  }, [competitions, filter, search, myGyms])

  const nothingAtAll = !loading && competitions.length === 0
  const nothingMatches = !loading && competitions.length > 0 && sections.length === 0

  return (
    <Page width="wide">
      <h1 className="sr-only">{t('competitions.pageTitle')}</h1>

      {!nothingAtAll && (
        <div className="flex flex-col gap-3">
          <ChipTabs
            aria-label={t('competitions.pageTitle')}
            value={filter}
            onChange={setFilter}
            options={[
              { key: 'all', label: t('competitions.filterAll') },
              { key: 'upcoming', label: t('competitions.filterUpcoming') },
              { key: 'finished', label: t('competitions.filterFinished') },
            ]}
          />
          <Input
            value={search}
            onValueChange={setSearch}
            placeholder={t('competitions.searchPlaceholder')}
            isClearable
            onClear={() => setSearch('')}
          />
        </div>
      )}

      {loading ? (
        <div className="flex flex-col gap-3">
          <SkeletonCard lines={3} />
          <SkeletonCard lines={3} />
        </div>
      ) : nothingAtAll || nothingMatches ? (
        <EmptyState
          icon="🏆"
          title={nothingAtAll ? t('competitions.empty') : t('competitions.noMatches')}
        />
      ) : (
        sections.map((section) => (
          <div key={section.key} className="flex flex-col gap-2">
            <p className="eyebrow mb-1 px-1 text-foreground/40">
              {section.key === 'mine'
                ? t('competitions.sectionMine')
                : section.key === 'myGyms'
                  ? t('competitions.sectionMyGyms')
                  : t('competitions.openToAll')}
            </p>
            {section.items.map((competition, i) => (
              <motion.div key={competition.id} {...staggerIndex(i)}>
                <CompetitionCard competition={competition} />
              </motion.div>
            ))}
          </div>
        ))
      )}
    </Page>
  )
}
